import { createContext, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@verlo/shared/contexts/AuthContext';
import { useApp } from '@verlo/shared/contexts/AppContext';
import { settingsApi } from '@verlo/shared/services/api';
import { OrgProvider } from '../contexts/OrgContext';
import { isNavItemActive } from '../lib/navActive';
import { UserDropdown } from '@verlo/shared/components/UserDropdown';
import { SiteProvider, useSite, useHasTill } from '../contexts/SiteContext';
import { AREAS, areaFor, pageSuitsPlan } from './office/areas';
import Jump, { useJump } from './office/Jump';
import { InsightProvider } from './office/Insight';
import BackOfficeAlerts from './office/Alerts';
import { Button } from '@verlo/shared/components/ui/button';
import { Menu, X, LogOut, Search } from 'lucide-react';

// ================================
// BACK OFFICE SIDEBAR NAV (cloud-ui)
// ================================
//
// The back office's whole chrome. The old single-tree `Layout.js` also carried the FOH
// horizontal header; that lives in `@verlo/node-ui` now (P7, plan §6). cloud-ui mounts no FOH
// route, so every `HAS_FOH` branch that drew a "Point Of Sale" link is gone — the till lives on
// the Pi, and there is no FOH route here to return to. `LOGIN_PATH` is the password login.

const LOGIN_PATH = '/login';


// ================================
// THE CHROME (Back Office Review, wave B; rebuilt to Martin's mock, 16 Sep)
// ================================
//
// Mock: https://claude.ai/artifact/YDm4egEKuL6PdrCoTXjSRj. Two materials: the navy rail and
// the paper everything else sits on. Three tiers, each attached to the next by *shape*,
// never by a coloured block:
//
//   - The rail (200px) lists the six areas as icon + name on one line. The selected area is
//     cut out in the page's paper and runs straight into the panel — a tab.
//   - The panel (200px) sits on the same paper, no fill behind anything; the selected page
//     is bold navy with a dot. A single hairline-strong divider runs the full height between
//     panel and page, and at the selected row it steps out to a point — the same stroke, a
//     few more anchors. It is measured from the active row, so it moves with the selection.
//   - The top line is one 56px band across all three columns and it IS the path: the
//     wordmark, the area, the page's title, then the site, Jump and who you are. The page's
//     title lives here (`usePageTitle`), so there is no separate breadcrumb and no second
//     H1 below. On an area's landing page (Today on Today) the area is not repeated.
//
// Navy is "where you are"; ink is content. The map is `office/areas.js`.

export const RAIL_WIDTH = 200;
export const PANEL_WIDTH = 200;
export const CHROME_WIDTH = RAIL_WIDTH + PANEL_WIDTH; // 400 — the header's left and main's margin
export const TOP_LINE = 56;

// The page's title, published by `PageHeader` (or any page) into the top line.
const PageTitleContext = createContext({ title: null, setTitle: () => {} });
export const usePageTitle = (title) => {
  const { setTitle } = useContext(PageTitleContext);
  useEffect(() => {
    setTitle(title ?? null);
    return () => setTitle(null);
  }, [title, setTitle]);
};

const allowed = (page, { isAdmin, hasPermission }, hasTill = true) => {
  if ((page.to.includes('staff') || page.to.includes('/settings') || page.to.includes('accounts')) && !isAdmin()) return false;
  if (page.perm && !hasPermission(page.perm)) return false;
  if (!pageSuitsPlan(page, hasTill)) return false;
  return true;
};

export const BackOfficeSidebar = () => {
  const auth = useAuth();
  const location = useLocation();
  const { area, page } = areaFor(location.pathname);
  const hasTill = useHasTill();
  const groups = area.groups.map((g) => ({ ...g, pages: g.pages.filter((p) => allowed(p, auth, hasTill)) })).filter((g) => g.pages.length);
  const isPageActive = (p) => isNavItemActive(location.pathname, p);

  // The divider points at the selected row: measure it, and again whenever the panel changes size.
  const panelRef = useRef(null);
  const listRef = useRef(null);
  const activeRef = useRef(null);
  const [geom, setGeom] = useState({ height: 0, point: null }); // the panel's height; the active row's { top, height } within it
  useLayoutEffect(() => {
    const measure = () => {
      const panel = panelRef.current;
      if (!panel) return;
      const p = panel.getBoundingClientRect();
      const a = activeRef.current?.getBoundingClientRect();
      // A row scrolled out of the list has no point to make.
      const visible = a && listRef.current && a.bottom > listRef.current.getBoundingClientRect().top && a.top < listRef.current.getBoundingClientRect().bottom;
      setGeom({ height: p.height, point: visible ? { top: a.top - p.top, height: a.height } : null });
    };
    // Bring the selected page into view first (a long list on a short screen), then measure.
    activeRef.current?.scrollIntoView?.({ block: 'nearest' });
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (ro && panelRef.current) ro.observe(panelRef.current);
    const list = listRef.current;
    list?.addEventListener('scroll', measure, { passive: true });
    return () => { ro?.disconnect(); list?.removeEventListener('scroll', measure); };
  }, [location.pathname, groups.length]);

  return (
    <aside className="hidden lg:flex fixed left-0 top-0 z-40 h-screen" data-testid="bo-sidebar">
      {/* The rail */}
      <div className="flex w-[200px] flex-col bg-verlo" data-testid="bo-rail">
        <NavLink to="/back-office" className="flex h-14 items-center pl-7 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-white" title="Verlo" data-testid="bo-wordmark">
          <img src="/verlo-logo-white.svg" alt="Verlo" className="h-6 w-auto" />
        </NavLink>
        <nav className="flex flex-col gap-0.5 pt-2" aria-label="Areas">
          {AREAS.map((a) => {
            const on = a.key === area.key;
            const first = a.groups.flatMap((g) => g.pages).find((p) => allowed(p, auth, hasTill));
            if (!first) return null;
            const Icon = a.icon;
            return (
              <NavLink
                key={a.key}
                to={first.to}
                className={`ml-4 flex h-11 items-center gap-3 rounded-l-md pl-3 pr-4 font-heading text-[14px] transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-white ${on ? 'bg-surface-2 font-bold text-verlo' : 'font-medium text-white/60 hover:bg-white/[0.08] hover:text-white/90'}`}
                aria-current={on ? 'page' : undefined}
                data-testid={`bo-area-${a.key}`}
              >
                {Icon && <Icon size={20} strokeWidth={on ? 2.25 : 1.75} />}
                {a.label}
              </NavLink>
            );
          })}
        </nav>
      </div>

      {/* The panel, on the page's paper, with the divider that points at the selected row */}
      <div ref={panelRef} className="relative flex w-[200px] flex-col bg-surface-2 pl-3" data-testid="bo-panel">
        <div className="flex h-14 shrink-0 items-center px-3" data-testid="bo-area-title">
          <span className="font-heading text-[20px] font-extrabold leading-tight tracking-[-0.01em] text-verlo">{area.label}</span>
        </div>
        {/* Scrolls when taller than the window, with no bar — the divider lives on that edge. */}
        <nav ref={listRef} className="bo-panel-list flex flex-1 flex-col gap-5 overflow-y-auto pb-4 pt-2" aria-label={area.label}>
          {groups.map((g, gi) => (
            <div key={g.label || gi} className={`flex flex-col ${gi > 0 && g.label ? 'mr-3 border-t border-hairline pt-5' : ''}`}>
              {/* A group label partitions the list; it is an eyebrow, not a page — same voice as a tile's label. */}
              {g.label && <div className="select-none pb-3.5 pl-6 pr-3 font-heading text-[11px] font-bold uppercase tracking-[0.08em] text-ink-faint">{g.label}</div>}
              {g.pages.map((p) => {
                const active = isPageActive(p);
                return (
                  <NavLink
                    key={p.to}
                    ref={active ? activeRef : undefined}
                    to={p.to}
                    end={p.exact}
                    // One item however many lines it takes: it grows with them (a fixed 30px let
                    // "Sales per labour hour" spill "hour" into the gap, where it read as an item
                    // of its own), and it's always as wide as its bold self, so being picked never
                    // wraps it ("Discounts & refunds" did). Back office sweep, Oct 2026.
                    className={`mr-1 flex min-h-[30px] items-center gap-2 rounded-md py-1 pl-2 pr-1.5 text-[13.5px] leading-[1.25] transition-colors ${active ? 'font-semibold text-verlo' : 'text-ink-soft hover:text-ink'}`}
                    aria-current={active ? 'page' : undefined}
                    data-testid={`bo-nav-${p.label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-$/, '')}`}
                  >
                    <span className={`h-1.5 w-1.5 shrink-0 self-start mt-[0.45em] rounded-full ${active ? 'bg-verlo' : ''}`} aria-hidden />
                    {/* The bold copy is CSS (`::after`), not text: it holds the width, and
                        the label stays the link's only words. */}
                    <span data-label={p.label} className="inline-grid min-w-0 after:invisible after:font-semibold after:content-[attr(data-label)] after:[grid-area:1/1]">
                      <span className="[grid-area:1/1]">{p.label}</span>
                    </span>
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>
        <Divider height={geom.height} point={geom.point} />
      </div>
    </aside>
  );
};

// One hairline the full height of the panel, drawn as a path so it can bend: straight down,
// out 10px to a point at the selected row and back. Same stroke throughout. Drawn in CSS
// pixels (no viewBox), so the point lands where the row was measured.
const Divider = ({ height, point }) => {
  const out = 10;
  const h = Math.max(height, 1);
  const y = point ? point.top + point.height / 2 : null;
  const d = point
    ? `M0.5 0 V${y - 13} L${out + 0.5} ${y} L0.5 ${y + 13} V${h}`
    : `M0.5 0 V${h}`;
  return (
    <svg className="pointer-events-none absolute top-0 -right-[10px] text-hairline-strong" width={out + 1} height={h} aria-hidden="true" data-testid="bo-divider" data-pointing={point ? 'true' : 'false'}>
      <path d={d} fill="none" stroke="currentColor" strokeWidth="1" strokeLinejoin="miter" />
    </svg>
  );
};

const SiteSwitcher = () => {
  const { sites, site, setSite, current } = useSite();
  if (sites.length < 2) return current ? <span className="text-[13px] font-medium text-ink" data-testid="bo-site">{current.name}</span> : null;
  return (
    <select
      value={site || ''}
      onChange={(e) => setSite(e.target.value || null)}
      className="h-8 rounded-md border border-hairline bg-white px-2 text-[13px] font-medium text-ink"
      aria-label="Site"
      data-testid="bo-site-switcher"
    >
      <option value="">All sites</option>
      {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
    </select>
  );
};

export const BackOfficeHeader = ({ onJump }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { area, page } = areaFor(location.pathname);
  const { title } = useContext(PageTitleContext);
  const heading = title || page?.label || area.label;

  const handleLogout = () => {
    logout();
    navigate(LOGIN_PATH);
  };

  return (
    <>
      {/* Desktop header. z-40, matching the sidebar (see #33 on stacking order). */}
      <header className="hidden lg:flex fixed top-0 left-[400px] right-0 z-40 h-14 items-center gap-4 bg-surface-2 pl-6 pr-6" data-testid="bo-header">
        <h1 className="min-w-0 truncate font-heading text-[20px] font-extrabold leading-tight tracking-[-0.01em] text-verlo" data-testid="bo-page-title">{heading}</h1>
        <div className="ml-auto flex items-center gap-3" data-testid="bo-header-tools">
          <SiteSwitcher />
          <button
            type="button"
            onClick={onJump}
            className="hidden h-8 items-center gap-2 rounded-md border border-hairline bg-white px-2.5 text-[13px] text-ink-faint hover:border-ink-faint hover:text-ink md:inline-flex"
            data-testid="bo-jump-btn"
          >
            <Search size={14} /> Jump to…
            <kbd className="rounded-md border border-hairline bg-surface-2 px-1 font-mono text-[11px] text-ink-faint">⌘K</kbd>
          </button>
          <UserDropdown user={user} onLogout={handleLogout} showBackOfficeLinks={true} />
        </div>
      </header>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-verlo flex items-center justify-between px-4 z-50" data-testid="bo-mobile-header">
        {/* Which page this is, on a phone too (Chris's review M11): the desktop header's
            title is hidden here. */}
        <div className="flex min-w-0 items-center gap-3">
          <img src="/verlo-logo-white.svg" alt="Verlo" className="h-5 shrink-0" />
          <span className="truncate font-heading text-[15px] font-bold text-white" data-testid="bo-mobile-title">{heading}</span>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            className="text-white h-8 w-8"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? 'Close the menu' : 'Menu'}
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </Button>
        </div>
      </header>

      {/* Mobile drawer: the six areas, every page */}
      {mobileMenuOpen && (
        <>
          <div className="lg:hidden fixed inset-0 bg-black/50 z-40" onClick={() => setMobileMenuOpen(false)} />
          <aside className="lg:hidden fixed top-0 left-0 h-full w-72 bg-white z-50 overflow-y-auto">
            <div className="p-5"><img src="/verlo-logo-blue.svg" alt="Verlo" className="h-5" /></div>
            <nav className="px-3 pb-4">
              {AREAS.map((a) => (
                <div key={a.key} className="mb-3">
                  <p className="px-2 py-1 font-heading text-[12.5px] font-semibold text-ink-soft">{a.label}</p>
                  <ul className="space-y-0.5">
                    {a.groups.flatMap((g) => g.pages).map((p) => (
                      <li key={p.to}>
                        <NavLink
                          to={p.to}
                          end={p.exact}
                          onClick={() => setMobileMenuOpen(false)}
                          className={({ isActive }) => `block rounded-md px-2 py-1.5 text-[13.5px] ${isActive ? 'bg-verlo-tint font-semibold text-verlo' : 'text-ink-soft'}`}
                        >
                          {p.label}
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </nav>
            <div className="border-t border-hairline p-4">
              <div className="flex items-center justify-between text-ink-soft">
                <span className="text-sm">{user?.name}</span>
                <Button variant="ghost" size="sm" onClick={handleLogout} className="text-ink-soft hover:text-ink">
                  <LogOut size={16} />
                </Button>
              </div>
            </div>
          </aside>
        </>
      )}
    </>
  );
};

export const BackOfficeLayout = ({ children }) => {
  // `OrgProvider` supplies the single resident org's branding (P6-FE-SWITCHER) to the back
  // office; `SiteProvider` the site the header's switcher picked. Both degrade safely.
  const jump = useJump();
  const [title, setTitle] = useState(null);
  // The venue's currency and VAT reach the app's formatter and `CurrencyIcon` from the
  // business settings, so a £ venue never shows a $ (the defaults are GBP until this lands).
  const { updateSettings } = useApp();
  useEffect(() => {
    settingsApi.get().then(({ data }) => {
      if (data?.currency) updateSettings({ currency: data.currency, ...(data.vat_rate != null ? { vatRate: data.vat_rate } : {}) });
    }).catch(() => {});
  }, [updateSettings]);
  return (
    <OrgProvider>
      <SiteProvider>
        <PageTitleContext.Provider value={{ title, setTitle }}>
        <InsightProvider>
        <div className="min-h-screen bg-surface-2 back-office-suite">
          <a href="#bo-main" className="sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-[60] focus:rounded-md focus:bg-verlo focus:px-3 focus:py-2 focus:text-sm focus:font-semibold focus:text-white" data-testid="bo-skip">
            Skip to content
          </a>
          <BackOfficeSidebar />
          <BackOfficeHeader onJump={() => jump.setOpen(true)} />
          <Jump open={jump.open} onOpenChange={jump.setOpen} />
          <BackOfficeAlerts />
          <main id="bo-main" tabIndex={-1} className="lg:ml-[400px] pt-14 min-h-screen outline-none">
            <div className="mx-auto max-w-[1280px] px-6 pb-6 pt-2">{children}</div>
          </main>
        </div>
        </InsightProvider>
        </PageTitleContext.Provider>
      </SiteProvider>
    </OrgProvider>
  );
};

export default BackOfficeLayout;
